import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ExternalLink, FileSignature, Loader2, RefreshCw, Search, XCircle } from 'lucide-react';
import { useAdminAuth } from '@/context/AdminAuthContext';
import { apiClient } from '@/lib/api-client';
import { formatUZS } from '@/lib/format';
import { apiErrorMessage, type Invoice } from '@/lib/invoices';
import { adminInvoiceKey, useAdminInvoice, useAdminInvoices, type AdminInvoiceTab } from '@/hooks/use-invoices';
import { InvoiceBadge } from '@/components/invoices/InvoiceBadge';
import { InvoiceDocument, SignDialog } from '@/components/invoices/InvoiceCard';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { intlLocale } from './adminLocale';

const TABS: AdminInvoiceTab[] = ['new', 'in_review', 'approved', 'rejected', 'all'];

/** Hujjat.uz callback events (Lovable _shared/hujjat-events.ts). */
const HUJJAT_EVENTS = ['FACTURA_RECEIVE', 'FACTURA_AGENT_ACCEPTED', 'FACTURA_ACCEPTED', 'FACTURA_REJECTED', 'FACTURA_CANCELLED', 'FACTURA_REVOKED_BY_GNK'] as const;
type HujjatEvent = (typeof HUJJAT_EVENTS)[number];

export default function AdminInvoices() {
  const { t } = useTranslation();
  const { admin, hasRole } = useAdminAuth();
  const isDirector = !!admin?.isDirector;
  // Lovable mock_callback: platform owner / director, MOCK mode only (the API refuses in REAL).
  const canTestCallback = isDirector || hasRole('platform_owner');
  const qc = useQueryClient();
  const [tab, setTab] = useState<AdminInvoiceTab>('new');
  const [search, setSearch] = useState('');
  const [merchantId, setMerchantId] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [openId, setOpenId] = useState<number | null>(null);

  const { data, isLoading } = useAdminInvoices({
    tab,
    search: search.trim() || undefined,
    merchant_id: merchantId === 'all' ? undefined : Number(merchantId),
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
  });
  const rows = data?.invoices ?? [];

  const { data: merchantsData } = useQuery({ queryKey: ['/admin/merchants'] });
  const merchants = (merchantsData as { data?: { id: number; name: string }[] } | undefined)?.data ?? [];

  const refreshList = () => qc.invalidateQueries({ queryKey: ['admin-invoices'] });

  return (
    <div className="p-4 md:p-6 space-y-5">
      <div className="flex flex-col gap-1">
        <h1 className="text-xl font-semibold text-foreground">{t('invoice.nav')}</h1>
        <p className="text-sm text-muted-foreground">
          {t('invoice.admin.subtitle')} {isDirector ? t('invoice.admin.director_hint') : t('invoice.admin.director_only')}
        </p>
      </div>

      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <Tabs value={tab} onValueChange={v => setTab(v as AdminInvoiceTab)}>
          <TabsList className="flex-wrap h-auto">
            {TABS.map(k => (
              <TabsTrigger key={k} value={k}>
                {t(`invoice.admin.tabs.${k}`)}{k === 'new' && data && data.pending > 0 ? ` (${data.pending})` : ''}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-full sm:w-60">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder={t('invoice.admin.search')} className="pl-9" />
          </div>
          <Select value={merchantId} onValueChange={setMerchantId}>
            <SelectTrigger className="w-[180px]"><SelectValue placeholder={t('invoice.admin.merchant')} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('invoice.admin.all_merchants')}</SelectItem>
              {merchants.map(m => <SelectItem key={m.id} value={String(m.id)}>{m.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-[150px]" aria-label={t('invoice.admin.date_from')} />
          <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-[150px]" aria-label={t('invoice.admin.date_to')} />
        </div>
      </div>

      <div className="bg-card border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-muted-foreground">
            <tr className="border-b border-border text-left">
              <th className="px-4 py-3 font-medium">{t('invoice.admin.doc')}</th>
              <th className="px-4 py-3 font-medium">{t('invoice.admin.merchant')}</th>
              <th className="px-4 py-3 font-medium">{t('invoice.status.sent')}</th>
              <th className="px-4 py-3 font-medium text-right">{t('invoice.admin.sum')}</th>
              <th className="px-4 py-3 font-medium">{t('invoice.admin.status')}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground"><Loader2 className="inline animate-spin" size={16} /></td></tr>}
            {!isLoading && rows.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">{t('invoice.admin.empty')}</td></tr>}
            {rows.map(r => (
              <tr key={r.id} onClick={() => setOpenId(r.id)} className="border-b border-border last:border-0 hover:bg-muted/40 cursor-pointer">
                <td className="px-4 py-3"><p className="font-medium text-foreground">{r.doc_num}</p><p className="text-xs text-muted-foreground">{r.doc_date}</p></td>
                <td className="px-4 py-3"><p className="text-foreground">{r.merchant?.name ?? '—'}</p><p className="text-xs text-muted-foreground font-mono">{r.merchant?.inn}</p></td>
                <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{r.sent_at ? new Date(r.sent_at).toLocaleString(intlLocale()) : '—'}</td>
                <td className="px-4 py-3 text-right font-medium whitespace-nowrap">{formatUZS(Number(r.final_sum))}</td>
                <td className="px-4 py-3"><InvoiceBadge status={r.status} director /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Sheet open={openId !== null} onOpenChange={v => { if (!v) { setOpenId(null); refreshList(); } }}>
        <SheetContent side="right" className="w-full sm:max-w-4xl overflow-y-auto">
          {openId !== null && <InvoiceDetail id={openId} isDirector={isDirector} canTestCallback={canTestCallback} onChanged={refreshList} />}
        </SheetContent>
      </Sheet>
    </div>
  );
}

export function InvoiceDetail({ id, isDirector, canTestCallback, onChanged }: { id: number; isDirector: boolean; canTestCallback: boolean; onChanged: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [signOpen, setSignOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [mockEvent, setMockEvent] = useState<HujjatEvent>('FACTURA_ACCEPTED');
  const { data: invoice, isLoading } = useAdminInvoice(id);

  const put = (next: Invoice) => { qc.setQueryData(adminInvoiceKey(id), next); onChanged(); };
  const reload = () => { qc.invalidateQueries({ queryKey: adminInvoiceKey(id) }); onChanged(); };

  // FR-87: the director opening a new invoice moves it into review.
  const status = invoice?.status;
  useEffect(() => {
    if (!isDirector || status !== 'sent') return;
    apiClient.POST('/admin/invoices/{invoice}/open', { params: { path: { invoice: id } } }).then(({ data }) => {
      if (data) put((data as unknown as { data: Invoice }).data);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isDirector, status]);

  const act = async (key: string, call: () => Promise<{ data?: unknown; error?: unknown }>, success: string) => {
    setBusy(key);
    try {
      const { data, error } = await call();
      if (error) throw error;
      put((data as { data: Invoice }).data);
      toast.success(t(success));
      return true;
    } catch (e) {
      toast.error(apiErrorMessage(e, t('common.error')));
      return false;
    } finally { setBusy(null); }
  };

  const refresh = () => act('refresh', () => apiClient.POST('/admin/invoices/{invoice}/refresh', { params: { path: { invoice: id } } }), 'invoice.toast.refreshed');
  const mockCallback = async () => {
    setBusy('mock');
    try {
      const { data, error } = await apiClient.POST('/admin/invoices/{invoice}/mock-callback', { params: { path: { invoice: id } }, body: { event: mockEvent } });
      if (error) throw error;
      const res = data as unknown as { result: string; data: Invoice };
      put(res.data);
      toast.success(t('invoice.admin.test_callback_done', { result: res.result }));
    } catch (e) {
      toast.error(apiErrorMessage(e, t('common.error')));
    } finally { setBusy(null); }
  };
  const reject = async () => {
    const ok = await act('reject', () => apiClient.POST('/admin/invoices/{invoice}/buyer-reject', { params: { path: { invoice: id } }, body: { reason: reason.trim() } as never }), 'invoice.toast.rejected');
    if (ok) { setRejectOpen(false); setReason(''); }
  };

  if (isLoading || !invoice) return <div className="py-10 text-center"><Loader2 className="inline animate-spin" /></div>;
  const actionable = ['sent', 'in_review'].includes(invoice.status);
  const buyerCanSign = invoice.buyer_signing !== false;

  return (
    <div className="space-y-4">
      <SheetHeader>
        <SheetTitle className="flex items-center gap-2"><FileSignature size={18} className="text-primary" /> {t('invoice.title')} {invoice.doc_num}</SheetTitle>
      </SheetHeader>
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-muted/30 p-3">
        <InvoiceBadge status={invoice.status} director />
        {invoice.merchant && <span className="text-xs text-muted-foreground">{invoice.merchant.name}</span>}
        {invoice.external_id && <span className="text-xs text-muted-foreground">Hujjat.uz ID: <span className="font-mono">{invoice.external_id}</span></span>}
        {invoice.last_event && <span className="text-xs text-muted-foreground">{t('invoice.admin.last_event')}: <span className="font-mono">{invoice.last_event}</span></span>}
        <div className="flex flex-wrap gap-2 md:ml-auto">
          <Button size="sm" variant="outline" onClick={refresh} disabled={!!busy}>
            {busy === 'refresh' ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <RefreshCw size={14} className="mr-1.5" />} {t('invoice.actions.refresh')}
          </Button>
          {canTestCallback && invoice.external_id && (
            <div className="flex items-center gap-1">
              <Select value={mockEvent} onValueChange={v => setMockEvent(v as HujjatEvent)}>
                <SelectTrigger className="h-9 w-[210px] text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {HUJJAT_EVENTS.map(ev => <SelectItem key={ev} value={ev} className="text-xs">{ev}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button size="sm" variant="outline" onClick={mockCallback} disabled={!!busy}>
                {busy === 'mock' && <Loader2 size={14} className="mr-1.5 animate-spin" />} {t('invoice.admin.test_callback')}
              </Button>
            </div>
          )}
          {isDirector && actionable && buyerCanSign && (
            <>
              <Button size="sm" variant="outline" className="text-destructive" onClick={() => setRejectOpen(true)} disabled={!!busy}><XCircle size={14} className="mr-1.5" /> {t('invoice.actions.reject')}</Button>
              <Button size="sm" onClick={() => setSignOpen(true)} disabled={!!busy}><FileSignature size={14} className="mr-1.5" /> {t('invoice.actions.buyer_sign')}</Button>
            </>
          )}
        </div>
      </div>

      {isDirector && actionable && !buyerCanSign && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-muted/30 p-3 text-sm" data-testid="hujjat-sign-hint">
          <span>{t('invoice.admin.sign_in_hujjat')}</span>
          {invoice.document_url && (
            <Button size="sm" variant="outline" asChild>
              <a href={invoice.document_url} target="_blank" rel="noopener noreferrer"><ExternalLink size={14} className="mr-1.5" /> {t('invoice.admin.open_in_hujjat')}</a>
            </Button>
          )}
        </div>
      )}

      <InvoiceDocument invoice={invoice} />

      {isDirector && buyerCanSign && <SignDialog buyer invoice={invoice} open={signOpen} onOpenChange={setSignOpen} onDone={reload} />}

      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('invoice.reject_dialog.title')}</DialogTitle>
          </DialogHeader>
          <Textarea value={reason} onChange={e => setReason(e.target.value)} maxLength={500} placeholder={t('invoice.reject_dialog.placeholder')} rows={4} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)}>{t('common.cancel')}</Button>
            <Button variant="destructive" onClick={reject} disabled={reason.trim().length < 3 || busy === 'reject'}>
              {busy === 'reject' && <Loader2 size={14} className="mr-1.5 animate-spin" />} {t('invoice.actions.reject')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
